import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppLayout } from '@/components/layout/AppLayout';
import { useCaregiverDetail } from '@/hooks/useCaregiverDetail';
import { useCompany } from '@/hooks/useCompany';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ChartContainer } from '@/components/ui/chart';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  LineChart, Line, Legend,
} from 'recharts';
import { CheckinPunctualityCalendar } from '@/components/company/CheckinPunctualityCalendar';
import {
  ClipboardCheck, Clock, AlertTriangle,
  Calendar, CheckCircle2, TrendingUp, Users, Filter,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const PERIOD_OPTIONS = [7, 15, 30] as const;

function KpiCard({ icon: Icon, label, value, color }: { icon: any; label: string; value: string | number; color?: string }) {
  return (
    <Card>
      <CardContent className="p-3 flex flex-col items-center text-center gap-1">
        <Icon className={`h-5 w-5 ${color || 'text-primary'}`} />
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="text-lg font-bold">{value}</span>
      </CardContent>
    </Card>
  );
}

function PctValue({ value }: { value: number | null }) {
  if (value === null) return <span className="text-muted-foreground">--</span>;
  const color = value >= 80 ? 'text-success' : value >= 70 ? 'text-warning' : 'text-destructive';
  return <span className={`font-bold ${color}`}>{value}%</span>;
}

export default function CaregiverDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const { t, i18n } = useTranslation();
  const { company } = useCompany();
  const [period, setPeriod] = useState<number>(7);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);

  // First fetch without patient filter to get the list of patients
  const { data: fullData } = useCaregiverDetail(company?.companyId, userId, period, null);
  // Then fetch with patient filter if selected
  const { data, isLoading } = useCaregiverDetail(company?.companyId, userId, period, selectedPatientId);

  const patientOptions = useMemo(() => {
    return fullData?.patients_served || [];
  }, [fullData?.patients_served]);

  const locale = i18n.language === 'pt-BR' ? ptBR : undefined;

  const barChartConfig = {
    on_time: { label: t('analytics.onTime'), color: 'hsl(var(--primary))' },
    late: { label: t('analytics.late'), color: 'hsl(var(--destructive))' },
  };

  const lineChartConfig = {
    task_pct: { label: t('caregiverDetail.taskPunctuality'), color: 'hsl(var(--primary))' },
  };


  const tooltipStyle = {
    backgroundColor: 'hsl(var(--card))',
    border: '1px solid hsl(var(--border))',
    borderRadius: '8px',
    color: 'hsl(var(--foreground))',
  };

  const axisTick = { fill: 'hsl(var(--muted-foreground))' };
  const tooltipLabelStyle = { color: 'hsl(var(--foreground))' };

  return (
    <AppLayout title={t('caregiverDetail.pageTitle')}>
      <div className="max-w-4xl mx-auto space-y-4 p-4 pb-24">

        {/* Caregiver Info */}
        <div className="flex items-center gap-3">
          {isLoading ? (
            <div className="flex items-center gap-3 flex-1">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="space-y-1">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
          ) : data ? (
            <div className="flex items-center gap-3 flex-1">
              <SignedAvatar
                src={data.profile.avatar_url}
                bucket="avatars"
                className="h-12 w-12"
                fallback={
                  <span className="bg-primary/10 text-primary text-lg flex items-center justify-center w-full h-full">
                    {data.profile.name.charAt(0).toUpperCase()}
                  </span>
                }
              />
              <div>
                <h2 className="text-lg font-bold">{data.profile.name}</h2>
                <p className="text-sm text-muted-foreground">
                  {t(`caregiverDetail.role_${data.profile.role}`)}
                  {data.profile.last_shift_date && (
                    <> · {t('analytics.lastShift')}: {format(new Date(data.profile.last_shift_date), 'dd/MM', { locale })}</>
                  )}
                </p>
              </div>
            </div>
          ) : null}
        </div>

        {/* Period filter + Patient filter */}
        <div className="overflow-x-auto -mx-4 px-4 scrollbar-none">
          <div className="flex gap-2 items-center w-max">
          {PERIOD_OPTIONS.map((p) => (
            <Button
              key={p}
              size="sm"
              variant={period === p ? 'default' : 'outline'}
              onClick={() => setPeriod(p)}
              className="whitespace-nowrap"
            >
              {t('analytics.lastNDays', { count: p })}
            </Button>
          ))}

          {patientOptions.length > 1 && (
            <Select
              value={selectedPatientId || 'all'}
              onValueChange={(v) => setSelectedPatientId(v === 'all' ? null : v)}
            >
              <SelectTrigger className="w-[160px] h-8 text-xs">
                <Filter className="h-3 w-3 mr-1" />
                <SelectValue placeholder={t('caregiverDetail.allPatients')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('caregiverDetail.allPatients')}</SelectItem>
                {patientOptions.map((p) => (
                  <SelectItem key={p.patient_id} value={p.patient_id}>
                    {p.nickname}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-24" />)}
            </div>
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
          </div>
        ) : data ? (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-3 gap-3">
              <KpiCard
                icon={ClipboardCheck}
                label={t('analytics.totalTasks')}
                value={data.kpis.total_tasks}
                color="text-primary"
              />
              <KpiCard
                icon={Clock}
                label={t('analytics.punctuality')}
                value={`${data.kpis.on_time_pct}%`}
                color={data.kpis.on_time_pct >= 80 ? 'text-success' : data.kpis.on_time_pct >= 50 ? 'text-warning' : 'text-destructive'}
              />
              <KpiCard
                icon={AlertTriangle}
                label={t('analytics.incidents')}
                value={data.kpis.incidents}
                color="text-destructive"
              />
              <KpiCard
                icon={Calendar}
                label={t('analytics.shifts')}
                value={data.kpis.shifts_count}
                color="text-primary"
              />
              <KpiCard
                icon={CheckCircle2}
                label={t('analytics.checkinPunctuality')}
                value={data.kpis.checkin_on_time_pct !== null ? `${data.kpis.checkin_on_time_pct}%` : '--'}
                color={data.kpis.checkin_on_time_pct === null ? 'text-muted-foreground' : data.kpis.checkin_on_time_pct >= 80 ? 'text-success' : data.kpis.checkin_on_time_pct >= 50 ? 'text-warning' : 'text-destructive'}
              />
              <KpiCard
                icon={TrendingUp}
                label={t('caregiverDetail.completionPct')}
                value={data.kpis.completion_pct !== null ? `${data.kpis.completion_pct}%` : '--'}
                color={data.kpis.completion_pct === null ? 'text-muted-foreground' : data.kpis.completion_pct >= 80 ? 'text-success' : data.kpis.completion_pct >= 50 ? 'text-warning' : 'text-destructive'}
              />
            </div>

            {/* Daily Activity Bar Chart */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">{t('analytics.dailyActivity')}</CardTitle>
              </CardHeader>
              <CardContent className="px-2 pb-3">
                <ChartContainer config={barChartConfig} className="h-64 w-full">
                  <BarChart data={data.daily_activity} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" tickFormatter={(v) => format(new Date(v + 'T12:00:00'), 'dd/MM')} fontSize={11} tick={axisTick} tickMargin={4} />
                    <YAxis allowDecimals={false} fontSize={11} tick={axisTick} width={40} />
                    <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} labelFormatter={(v) => format(new Date(v + 'T12:00:00'), 'dd/MM/yyyy')} />
                    <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
                    <Bar dataKey="on_time" name={t('analytics.onTime')} stackId="a" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="late" name={t('analytics.late')} stackId="a" fill="hsl(var(--destructive))" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ChartContainer>
              </CardContent>
            </Card>

            {/* Task Punctuality Trend Line Chart */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">{t('caregiverDetail.punctualityTrend')}</CardTitle>
              </CardHeader>
              <CardContent className="px-2 pb-3">
                <ChartContainer config={lineChartConfig} className="h-64 w-full">
                  <LineChart data={data.daily_punctuality.filter(d => d.task_pct !== null)} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" tickFormatter={(v) => format(new Date(v + 'T12:00:00'), 'dd/MM')} fontSize={11} tick={axisTick} tickMargin={4} />
                    <YAxis domain={[0, 100]} fontSize={11} tickFormatter={(v) => `${v}%`} tick={axisTick} width={44} />
                    <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} labelFormatter={(v) => format(new Date(v + 'T12:00:00'), 'dd/MM/yyyy')} formatter={(v: number) => `${v}%`} />
                    <Line type="monotone" dataKey="task_pct" name={t('caregiverDetail.taskPunctuality')} stroke="hsl(var(--primary))" strokeWidth={2} dot={false} connectNulls />
                  </LineChart>
                </ChartContainer>
              </CardContent>
            </Card>

            {/* Check-in Punctuality Calendar */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">{t('caregiverDetail.checkinTrend')}</CardTitle>
              </CardHeader>
              <CardContent>
                <CheckinPunctualityCalendar data={data.daily_punctuality} periodDays={period} dailyShifts={data.daily_shifts} />
              </CardContent>
            </Card>

            {/* Patients Served */}
            {data.patients_served.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" />
                    {t('caregiverDetail.patientsServed')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto scrollbar-none -mx-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('analytics.patient')}</TableHead>
                          <TableHead className="text-center">{t('caregiverDetail.shiftsCount')}</TableHead>
                          <TableHead className="text-center">{t('analytics.tasks')}</TableHead>
                          <TableHead className="text-center">{t('caregiverDetail.taskPunctuality')}</TableHead>
                          <TableHead className="text-center">{t('caregiverDetail.checkinPunctuality')}</TableHead>
                          <TableHead className="text-center">{t('caregiverDetail.completionPct')}</TableHead>
                          <TableHead className="text-center">{t('analytics.incidentsCol')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.patients_served.map((p) => (
                          <TableRow key={p.patient_id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <SignedAvatar
                                  src={p.photo_url}
                                  bucket="patient-photos"
                                  className="h-7 w-7"
                                  fallback={
                                    <span className="bg-primary/10 text-primary text-xs flex items-center justify-center w-full h-full">
                                      {p.nickname.charAt(0).toUpperCase()}
                                    </span>
                                  }
                                />
                                <span className="text-sm truncate max-w-[140px]">{p.nickname}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-center font-medium">{p.shifts_count}</TableCell>
                            <TableCell className="text-center font-medium">{p.tasks}</TableCell>
                            <TableCell className="text-center">
                              {p.task_on_time_pct !== null ? (
                                <span className={p.task_on_time_pct >= 80 ? 'text-success' : p.task_on_time_pct >= 50 ? 'text-warning' : 'text-destructive'}>
                                  {p.task_on_time_pct}%
                                </span>
                              ) : '—'}
                            </TableCell>
                            <TableCell className="text-center">
                              {p.checkin_on_time_pct !== null ? (
                                <span className={p.checkin_on_time_pct >= 80 ? 'text-success' : p.checkin_on_time_pct >= 50 ? 'text-warning' : 'text-destructive'}>
                                  {p.checkin_on_time_pct}%
                                </span>
                              ) : '—'}
                            </TableCell>
                            <TableCell className="text-center">
                              {p.completion_pct !== null ? (
                                <span className={p.completion_pct >= 80 ? 'text-success' : p.completion_pct >= 50 ? 'text-warning' : 'text-destructive'}>
                                  {p.completion_pct}%
                                </span>
                              ) : '—'}
                            </TableCell>
                            <TableCell className="text-center">{p.incidents}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        ) : null}
      </div>
    </AppLayout>
  );
}
